import i18n, { LOCALIZED_PACK_UNITS } from '@/utils/i18n';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import {
  DEFAULT_CURRENCY_CODE,
  formatCurrency,
  roundMicrosToNearestWholeCent,
} from '@/utils/money';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { MultiStyleText } from './multi-style-text';
import { PackUnit } from '@/enums';
import { PeakPriceShield } from './orders/peak-price-shield';
import { Text, View } from 'react-native';
import { useMaybePaymentsContext } from '@/contexts/payments-context';

const TEXT_LINE_HEIGHT = 18;

interface Props {
  baseUnit?: PackUnit | null;
  currencyCode: string | null;
  displayString: string;
  isSalesRepPeakPriceProtectionInEffect?: boolean;
  modifierString?: string;
  promotionPriceInMicros?: number | null;
  showInlineModifiers: boolean;
  sku?: string | null;
  unitCount: number;
  unitPriceInMicros?: number | null;
}

export const ItemSubtitle: React.FC<Props> = ({
  baseUnit,
  currencyCode,
  displayString,
  isSalesRepPeakPriceProtectionInEffect,
  modifierString,
  promotionPriceInMicros,
  showInlineModifiers,
  sku,
  unitCount,
  unitPriceInMicros,
}) => {
  const [styles] = useStyles();
  const paymentsContext = useMaybePaymentsContext();
  const defaultCurrencyCode =
    paymentsContext?.currencyCode || DEFAULT_CURRENCY_CODE;
  const finalCurrencyCode = currencyCode || defaultCurrencyCode;

  // TOP MIDDLE ROWS
  const skuText = sku ? (
    <View style={styles.skuWrapper}>
      <Text
        key="sku-text"
        numberOfLines={1}
        style={[TYPOGRAPHY.listSmall, styles.skuText]}
      >
        {sku}
      </Text>
    </View>
  ) : null;

  const modifierText = modifierString ? (
    <Text
      key="modifier-text"
      style={[TYPOGRAPHY.listSubtitle, styles.primaryText, styles.modifierText]}
    >
      {showInlineModifiers ? ' · ' : ''}
      {modifierString}
    </Text>
  ) : null;

  let topRow: React.ReactNode | null = null;
  let middleRow: React.ReactNode | null = null;
  if (showInlineModifiers) {
    topRow = skuText;
    middleRow = (
      <Text
        key="display-string-modifier-text"
        numberOfLines={1}
        style={[TYPOGRAPHY.listSubtitle, styles.primaryText]}
      >
        {displayString}
        {modifierText}
      </Text>
    );
  } else {
    topRow = (
      <View key="display-string-sku" style={styles.flexRow}>
        <Text
          numberOfLines={1}
          style={[TYPOGRAPHY.listSubtitle, styles.primaryText]}
        >
          {displayString}
        </Text>
        <View style={displayString ? styles.skuTextContainer : null}>
          {skuText}
        </View>
      </View>
    );
    middleRow = modifierText;
  }

  // BOTTOM ROW
  const bottomRowComponents: React.ReactNode[] = [];
  if (promotionPriceInMicros) {
    bottomRowComponents.push(
      <Text key="promotion-price" style={FONTS.primaryBold}>
        {formatCurrency(unitCount * promotionPriceInMicros, finalCurrencyCode)}
      </Text>,
    );
  }

  const showOnlyPromoPrice =
    typeof promotionPriceInMicros === 'number' &&
    unitPriceInMicros &&
    roundMicrosToNearestWholeCent(unitPriceInMicros) ===
      roundMicrosToNearestWholeCent(promotionPriceInMicros);

  if (typeof unitPriceInMicros === 'number' && !showOnlyPromoPrice) {
    bottomRowComponents.push(
      <Text
        key="unit-price"
        style={
          promotionPriceInMicros ? styles.strikethroughText : FONTS.primaryBold
        }
      >
        {formatCurrency(unitCount * unitPriceInMicros, finalCurrencyCode)}
      </Text>,
    );

    if (baseUnit) {
      const displayUnit = (
        LOCALIZED_PACK_UNITS[baseUnit] || baseUnit
      ).toLowerCase();
      const displayUnitPrice = formatCurrency(
        unitPriceInMicros,
        finalCurrencyCode,
      );

      bottomRowComponents.push(<Text key="per-unit-space">&nbsp;</Text>);
      bottomRowComponents.push(
        <MultiStyleText
          defaultStyle={[TYPOGRAPHY.listSmall, styles.perUnitText]}
          emphasisStyle={[TYPOGRAPHY.listSmall, styles.perUnitStrikethrough]}
          key="per-unit-price"
        >
          {promotionPriceInMicros
            ? i18n.t('per_unit_promo_price', {
                previousPrice: displayUnitPrice,
                promoPrice: formatCurrency(
                  promotionPriceInMicros,
                  finalCurrencyCode,
                ),
                unit: displayUnit,
              })
            : i18n.t('per_unit_price', {
                price: displayUnitPrice,
                unit: displayUnit,
              })}
        </MultiStyleText>,
      );
    }
  }

  if (isSalesRepPeakPriceProtectionInEffect) {
    bottomRowComponents.push(
      <PeakPriceShield
        key="peak-price-shield"
        style={styles.peakPriceShieldSpacing}
      />,
    );
  }

  const bottomRow = bottomRowComponents.length ? (
    <Text
      numberOfLines={1}
      style={[TYPOGRAPHY.listSubtitle, styles.primaryText]}
    >
      {bottomRowComponents.reduce<React.ReactNode[]>(
        (res, component) => (res.length ? [res, ' ', component] : [component]),
        [],
      )}
    </Text>
  ) : null;

  return (
    <View style={styles.base}>
      {topRow}
      {middleRow}
      {bottomRow}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    alignSelf: 'stretch',
    flex: 1,
    justifyContent: 'center',
    marginRight: 16,
  },
  flexRow: {
    flexDirection: 'row',
  },
  modifierText: {
    textTransform: 'none',
  },
  peakPriceShieldSpacing: {
    marginHorizontal: 4,
    textTransform: 'none',
  },
  perUnitStrikethrough: {
    color: theme.colors.inactiveText,
    textDecorationLine: 'line-through',
    textTransform: 'lowercase',
  },
  perUnitText: {
    color: theme.colors.inactiveText,
    textTransform: 'lowercase',
  },
  primaryText: {
    lineHeight: TEXT_LINE_HEIGHT,
    textTransform: 'uppercase',
  },
  skuText: {
    ...FONTS.primaryBold,
    color: theme.colors.inactiveText,
    lineHeight: TEXT_LINE_HEIGHT,
  },
  skuTextContainer: {
    flex: 1,
    marginLeft: 6,
  },
  skuWrapper: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  strikethroughText: {
    textDecorationLine: 'line-through',
  },
}));
